/**
 * The booking column on a service page: the price and terms, a month calendar
 * of the service's open days, the chosen day's open times in the visitor's own
 * time zone, and one Continue that carries the pick to the checkout address.
 */
import type { SiteEventDto, SiteOpenTime, SiteServiceDto, SiteUserBooking } from '@telify/shared';

import { definePiece } from '../../contract/define';

export const contract = definePiece({
  name: 'booking-column',
  description: "A service's price, terms, open days and open times, and Continue to the checkout with the chosen time.",
  placeable: true,
  options: {
    showPrice: { type: 'boolean', default: true, description: 'Show the price. Required: cannot be hidden.', showsPart: 'price' },
    showTerms: { type: 'boolean', default: true, description: 'Show the payment terms line. Required: cannot be hidden.', showsPart: 'terms' },
    showZone: { type: 'boolean', default: true, description: "Show the visitor's time zone under the times.", showsPart: 'zone' },
    continueLabel: { type: 'string', default: 'Continue', maxLength: 40, description: 'The Continue button text.' },
    service: {
      type: 'string',
      default: null,
      maxLength: 64,
      requiredWhenPlaced: true,
      description: "The id of the service to book. Telify's service page takes it from its address; a custom page sets it.",
    },
  },
  parts: {
    column: { description: 'The whole column.', required: false },
    format: { description: 'The kind line: session, in person, online or by phone.', required: false },
    price: { description: 'The price, Free, or from the lowest price.', required: true },
    terms: { description: 'How payment works, where the studio set terms.', required: true },
    approval: { description: 'The note that the studio approves each request, where it does.', required: true },
    calendar: { description: 'The month calendar.', required: true },
    month: { description: 'The month name above the calendar.', required: false },
    'month-prev': { description: 'The earlier-month button.', required: false, states: ['disabled'] },
    'month-next': { description: 'The later-month button.', required: false },
    day: { description: 'One day of the month; open days can be chosen.', required: true, states: ['selected', 'open', 'disabled'] },
    times: { description: "The chosen day's open times.", required: true },
    time: { description: 'One open time.', required: true, states: ['selected'], role: 'time' },
    'no-times': { description: 'The line shown when the chosen day has no open time left.', required: false },
    zone: { description: "The visitor's time zone, named in words.", required: false },
    continue: { description: 'Continue to the checkout with the chosen time.', required: true, states: ['disabled'], role: 'primary-action' },
    note: { description: 'The line under Continue.', required: false },
    weekday: { description: 'One weekday name over the calendar.', required: false },
    'day-label': { description: 'The chosen day, named in words, above its times.', required: false },
    'pick-note': { description: 'The line asking for a time while none is chosen.', required: false },
    summary: { description: 'The chosen date and time and the total, once a time is chosen.', required: false },
    paused: { description: 'The line saying the studio is not taking new bookings right now.', required: false },
    spots: { description: 'How many places an event has left.', required: false, states: ['low'] },
    'event-time': { description: "An event's fixed date and time, in place of the calendar.", required: false },
    closed: { description: 'The line saying an event is full or closed to booking.', required: false },
    'view-booking': { description: "View booking, in place of Continue, when the signed-in client already booked the event.", required: true },
  },
  actions: {
    showMonth: { description: 'Shows another month of open days.', args: [{ name: 'month', kind: 'value', type: 'string', format: 'month' }] },
    pickDay: { description: 'Chooses a day and loads its open times.', args: [{ name: 'day', kind: 'item', list: 'openDays' }] },
    pickTime: { description: 'Chooses one open time.', args: [{ name: 'startsAt', kind: 'item', list: 'times', key: 'startsAt' }] },
    continue: { description: 'Goes to the checkout address with the service and the chosen time.', args: [] },
    viewBooking: { description: "Opens the client's own booking of the event.", args: [] },
  },
  events: {
    picked: 'A visitor chose a time.',
    continued: 'A visitor pressed Continue with a time chosen.',
    eventContinued: 'A visitor pressed Continue on an event.',
  },
});

/** What the booking column knows. */
export interface BookingColumnState {
  /** loading until the service's first open days land; error when a read failed. */
  readonly status: 'loading' | 'ready' | 'error';
  /** The service being booked, as its page reads it. */
  readonly service: SiteServiceDto | null;
  /** The visitor's IANA time zone; every day and time below is in it. */
  readonly timezone: string;
  /** That zone named in words, such as "Eastern Time (EDT)". */
  readonly zoneName: string;
  /** The month the calendar shows, YYYY-MM. */
  readonly month: string;
  /** The days of that month with at least one open time, YYYY-MM-DD. */
  readonly openDays: readonly string[];
  /** The chosen day, YYYY-MM-DD, or null. */
  readonly selectedDay: string | null;
  /** loading while the chosen day's times load. */
  readonly timesStatus: 'idle' | 'loading' | 'ready' | 'error';
  /** The chosen day's open times, as UTC instants. */
  readonly times: readonly SiteOpenTime[];
  /** The chosen time's start, a UTC instant, or null. */
  readonly selectedStart: string | null;
  /** True when a time is chosen and the studio takes bookings now. */
  readonly canContinue: boolean;
  /** What the column books: a service (a calendar of open times) or an event (its one fixed time; Continue goes to the checkout address with the event, unless it is full or closed). */
  readonly kind: 'service' | 'event';
  /** The event being booked, as its page reads it, when `kind` is event; else null. */
  readonly event: SiteEventDto | null;
  /** Today in the visitor's zone, YYYY-MM-DD; no earlier month can be shown. */
  readonly today: string;
  /** True while the studio has paused new bookings: no time can be chosen and there is no Continue. */
  readonly paused: boolean;
  /** Why an event cannot be booked: full (no place left), closed (registration closed or too late), or null when it can. */
  readonly eventClosed: 'full' | 'closed' | null;
  /** The signed-in client's own booking of the event; null when there is none. It comes first, ahead of full and closed (the app's order): View Booking in place of Continue and the closed line. */
  readonly booked: SiteUserBooking | null;
  /** True for a signed-in client: the line under Continue about signing in is a visitor's only. */
  readonly signedIn: boolean;
}

/** What a drawing can ask the booking column to do. */
export interface BookingColumnActions {
  /** Show the month YYYY-MM. */
  readonly showMonth: (month: string) => void;
  /** Choose the day YYYY-MM-DD; an unopen day is ignored. */
  readonly pickDay: (day: string) => void;
  /** Choose the open time starting at this UTC instant; any other value is ignored. */
  readonly pickTime: (startsAt: string) => void;
  /** Go to the checkout address with the service and the chosen time; does nothing without one. */
  readonly continue: () => void;
  /** Open the client's own booking of the event; does nothing without one. */
  readonly viewBooking: () => void;
}

/** What the booking column tells its host. */
export interface BookingColumnEvents {
  /** A visitor chose a time. */
  readonly picked: { readonly serviceId: string; readonly startsAt: string };
  /** A visitor pressed Continue. */
  readonly continued: { readonly serviceId: string; readonly startsAt: string };
  /** A visitor pressed Continue on an event; carries its id. */
  readonly eventContinued: { readonly eventId: string };
}
